"use client";

import React, { useState, useCallback } from "react";
import { useSearchParams } from "next/navigation";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Search,
  ChevronUp,
  ChevronDown,
  Copy,
  Plus,
  Trash,
  SortDesc,
  Filter,
  Check,
  Loader2,
  FileDown,
  ListFilter,
  LayoutGrid,
  MoreVertical,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Drawer, DrawerContent, DrawerTrigger } from "@/components/ui/drawer";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { DataItem, Product } from "@/types";
import { exportToCSV } from "@/lib/utils/csv";
import { exportProductsToCin7 } from "@/lib/exporters/cin7";
import {
  format,
  isToday,
  isYesterday,
  isThisWeek,
  isThisMonth,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
  subDays,
  subMonths,
  isWithinInterval,
} from "date-fns";
import { usePermissions } from "@/hooks/use-permissions";
import { Resource } from "@/config/resources";
import DateFilter from "./date-filter";

interface GroupMarkerProps {
  label: string;
}

const GroupMarker = ({ label }: GroupMarkerProps) => (
  <div className='sticky top-[60px] z-10 px-4 py-2 font-medium bg-background text-muted-foreground text-left'>
    {label}
  </div>
);

const getGroupKey = (
  item: Record<string, unknown>,
  sortField: string
): { key: string; display: string } => {
  const value = item[sortField];

  // Handle date fields (created, updated)
  if (
    sortField === "created" ||
    sortField === "updated" ||
    sortField === "timestamp"
  ) {
    if (!value) return { key: "unknown", display: "Unknown Date" };

    try {
      const date =
        value instanceof Date ? value : new Date(value as string | number);

      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return { key: "unknown", display: "Unknown Date" };
      }

      if (isToday(date)) return { key: "today", display: "Today" };
      if (isYesterday(date)) return { key: "yesterday", display: "Yesterday" };
      if (isThisWeek(date)) return { key: "this-week", display: "This Week" };
      if (isThisMonth(date))
        return { key: "this-month", display: "This Month" };

      // Return the year-month as a sortable key to maintain order
      return {
        key: format(date, "yyyy-MM"),
        display: format(date, "MMMM yyyy"),
      };
    } catch (error) {
      console.error("Error parsing date:", error);
      return { key: "unknown", display: "Unknown Date" };
    }
  }

  // Special handling for specific fields
  if (
    sortField === "status" ||
    sortField === "order_status" ||
    sortField === "type" ||
    sortField === "category" ||
    sortField === "priority"
  ) {
    if (!value) return { key: "unknown", display: "Unknown" };
    const stringValue = String(value);

    // Format for display (capitalize and replace hyphens with spaces)
    const displayValue = stringValue
      .split(/[-_]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

    return { key: stringValue, display: displayValue };
  }

  // Default handling for other string fields
  if (typeof value === "string") {
    const firstChar = value.trim().charAt(0).toUpperCase();
    if (!firstChar) return { key: "other", display: "#" };
    if (/[0-9]/.test(firstChar)) return { key: "0-9", display: "0-9" };
    return { key: firstChar, display: firstChar };
  }

  return { key: "other", display: "Other" };
};

const hasGroupChanged = (
  current: Record<string, unknown>,
  previous: Record<string, unknown> | null,
  sortField: string
): boolean => {
  if (!previous) return true;

  const currentGroup = getGroupKey(current, sortField);
  const previousGroup = getGroupKey(previous, sortField);

  return currentGroup.key !== previousGroup.key;
};

interface DataViewerProps<T extends DataItem> {
  data: T[];
  formComponent: React.FC<{
    data: T | null;
    onSave: (data: T) => Promise<T>;
    onCancel: () => void;
    isNew?: boolean;
    canUpdate?: boolean;
  }>;
  onUpdate?: (id: string, data: T) => Promise<T>;
  onCreate?: (data: T) => Promise<T>;
  onDelete?: (id: string | string[]) => Promise<void>;
  onDuplicate?: (data: T) => Promise<T>;
  getItemId: (item: T) => string;
  getItemTitle: (item: T) => string;
  getItemImage?: (item: T) => string;
  filterableFields: Array<keyof T>;
  exportableFields?: Array<keyof T>;
  renderItem?: (item: T, isSelected: boolean, index: number) => React.ReactNode;
  renderItemActions?: (item: T) => React.ReactNode;
  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
  exportFilename?: string;
  initialSortField?: keyof T;
  initialSortOrder?: "asc" | "desc";
  resource?: Resource;
  showCheckboxSelection?: boolean;
  customFilter?: (item: T, searchTerm: string) => boolean;
  dateFilterField?: keyof T; // Field to use for date filtering (e.g., 'created', 'updated')
  showDateFilter?: boolean; // Whether to show the date filter component
}

type OperationStatus = {
  type: "create" | "update" | "delete" | "duplicate";
  status: "pending" | "success";
  timestamp: number;
};

const RATE_LIMIT_DELAY = 1000; // 1 second delay between duplicate actions

interface ToastProps {
  title: string;
  description: string;
  variant: "default" | "destructive" | "success";
}

// Modify the data-viewer search and filter controls to include a grouping toggle
const SearchFilterControls = <T extends DataItem>({
  searchTerm,
  setSearchTerm,
  showFilters,
  setShowFilters,
  filterableFields,
  sortField,
  setSortField,
  sortOrder,
  setSortOrder,
  groupingEnabled,
  setGroupingEnabled,
  renderCreateButton,
  isMobile,
}: {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  showFilters: boolean;
  setShowFilters: (value: boolean) => void;
  filterableFields: Array<keyof T>;
  sortField: keyof T;
  setSortField: (field: keyof T) => void;
  sortOrder: "asc" | "desc";
  setSortOrder: (order: "asc" | "desc") => void;
  groupingEnabled: boolean;
  setGroupingEnabled: (enabled: boolean) => void;
  renderCreateButton: () => React.ReactNode;
  isMobile: boolean;
}) => (
  <div className='sticky top-0 z-50 bg-background pb-4 pt-1 border-b px-4 md:px-0'>
    <div className='flex flex-col space-y-4 md:flex-row md:space-x-4 md:space-y-0'>
      <div className='flex space-x-2 flex-1'>
        <div className='relative flex-1'>
          <Search className='absolute left-2 top-2.5 h-4 w-4 text-muted-foreground' />
          <Input
            placeholder='Search...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm("")}
            className='pl-8'
            id='data-viewer-search'
            name='search'
          />
        </div>
        {isMobile ? (
          <>
            <Drawer open={showFilters} onOpenChange={setShowFilters}>
              <DrawerTrigger asChild>
                <Button variant='outline' size='icon'>
                  <Filter className='h-4 w-4' />
                </Button>
              </DrawerTrigger>
              <DrawerContent className='h-fit max-h-[60vh] z-[101] pb-4'>
                <div className='space-y-4 mt-2'>
                  <h3 className='font-semibold text-2xl text-center pt-4'>
                    Sort by
                  </h3>
                  <div className='space-y-4'>
                    <ScrollArea className='h-fit max-h-[60vh]'>
                      <div className='flex flex-col space-y-2'>
                        {filterableFields.map((field) => (
                          <Button
                            key={String(field)}
                            variant='ghost'
                            className={`justify-center w-full ${
                              sortField === field
                                ? "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
                                : ""
                            }`}
                            onClick={() => setSortField(field as keyof T)}
                          >
                            {String(field).charAt(0).toUpperCase() +
                              String(field).slice(1)}
                          </Button>
                        ))}
                      </div>
                      <div className='pt-4 border-t mt-4'>
                        <h4 className='font-medium text-center text-sm mb-2'>
                          Display Options
                        </h4>
                        <Button
                          variant='outline'
                          onClick={() => setGroupingEnabled(!groupingEnabled)}
                          className='w-full'
                        >
                          {groupingEnabled ? (
                            <>
                              <ListFilter className='mr-2 h-4 w-4' />
                              Hide Groups
                            </>
                          ) : (
                            <>
                              <LayoutGrid className='mr-2 h-4 w-4' />
                              Show Groups
                            </>
                          )}
                        </Button>
                      </div>
                    </ScrollArea>
                  </div>
                </div>
              </DrawerContent>
            </Drawer>
            <Button
              variant='outline'
              size='icon'
              onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
            >
              {sortOrder === "asc" ? (
                <ChevronUp className='h-4 w-4' />
              ) : (
                <ChevronDown className='h-4 w-4' />
              )}
            </Button>
          </>
        ) : (
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant='outline' size='icon'>
                  <SortDesc className='h-4 w-4' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {filterableFields.map((field) => (
                  <DropdownMenuItem
                    key={String(field)}
                    onClick={() => setSortField(field as keyof T)}
                    className={`justify-center w-full ${
                      sortField === field
                        ? "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
                        : ""
                    }`}
                  >
                    {String(field).charAt(0).toUpperCase() +
                      String(field).slice(1)}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant='outline'
              size='icon'
              onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
            >
              {sortOrder === "asc" ? (
                <ChevronUp className='h-4 w-4' />
              ) : (
                <ChevronDown className='h-4 w-4' />
              )}
            </Button>
            <Button
              variant='outline'
              size='icon'
              onClick={() => setGroupingEnabled(!groupingEnabled)}
              title={groupingEnabled ? "Hide Groups" : "Show Groups"}
            >
              {groupingEnabled ? (
                <ListFilter className='h-4 w-4' />
              ) : (
                <LayoutGrid className='h-4 w-4' />
              )}
            </Button>
          </>
        )}
      </div>
      <div className='flex space-x-2'>{renderCreateButton()}</div>
    </div>
  </div>
);

function DataViewer<T extends DataItem>(props: DataViewerProps<T>) {
  const {
    data: initialData,
    formComponent: FormComponent,
    onUpdate,
    onCreate,
    onDelete,
    onDuplicate,
    getItemId,
    getItemTitle,
    getItemImage,
    filterableFields,
    exportableFields,
    renderItem,
    renderItemActions,
    canCreate = true,
    canUpdate = true,
    canDelete = true,
    exportFilename = "export",
    initialSortField,
    initialSortOrder = "desc",
    resource,
    showCheckboxSelection = true,
    customFilter,
    dateFilterField,
    showDateFilter = false,
  } = props;

  const { checkPermission } = usePermissions();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const searchParams = useSearchParams();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<keyof T>(
    initialSortField || ("updated" as keyof T)
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">(initialSortOrder);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [operationStatus, setOperationStatus] = useState<
    Record<string, OperationStatus>
  >({});
  const [newItem, setNewItem] = useState<T | null>(null);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [deleteAlertOpen, setDeleteAlertOpen] = useState<string | null>(null);
  const [actionDrawerOpen, setActionDrawerOpen] = useState<string | null>(null);
  const [actionDrawerHidden, setActionDrawerHidden] = useState(false);
  const [bulkDrawerOpen, setBulkDrawerOpen] = useState(false);
  const [bulkDrawerHidden, setBulkDrawerHidden] = useState(false);
  const [bulkDeleteAlertOpen, setBulkDeleteAlertOpen] = useState(false);
  const [lastDuplicateTime, setLastDuplicateTime] = useState<number>(0);
  const [deletedItems, setDeletedItems] = useState<Set<string>>(new Set());
  const [duplicatingItems, setDuplicatingItems] = useState<string[]>([]);
  const [visibleItems, setVisibleItems] = useState(50); // Start with 50 items
  const [totalItems, setTotalItems] = useState(0);
  const [groupingEnabled, setGroupingEnabled] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(true);
  const [skipDeleteConfirm, setSkipDeleteConfirm] = useState(false);

  // Check permissions if resource is provided
  const hasCreatePermission = resource
    ? checkPermission(resource, "create")
    : canCreate;
  const hasUpdatePermission = resource
    ? checkPermission(resource, "update")
    : canUpdate;
  const hasDeletePermission = resource
    ? checkPermission(resource, "delete")
    : canDelete;

  // Helper function to get date range based on URL parameters
  const getDateRange = useCallback(() => {
    if (!showDateFilter || !dateFilterField) return null;

    const period = searchParams.get("period") || "all";
    const startDateStr = searchParams.get("startDate");
    const endDateStr = searchParams.get("endDate");

    if (period === "all") return null;

    if (period === "custom" && startDateStr && endDateStr) {
      return {
        start: startOfDay(new Date(startDateStr)),
        end: endOfDay(new Date(endDateStr)),
      };
    }

    const now = new Date();

    switch (period) {
      case "today":
        return {
          start: startOfDay(now),
          end: endOfDay(now),
        };
      case "yesterday":
        const yesterday = subDays(now, 1);
        return {
          start: startOfDay(yesterday),
          end: endOfDay(yesterday),
        };
      case "week":
        return {
          start: startOfWeek(now),
          end: endOfWeek(now),
        };
      case "month":
        return {
          start: startOfMonth(now),
          end: endOfMonth(now),
        };
      case "3months":
        return {
          start: startOfMonth(subMonths(now, 2)),
          end: endOfMonth(now),
        };
      case "quarter":
        return {
          start: startOfQuarter(now),
          end: endOfQuarter(now),
        };
      case "6months":
        return {
          start: startOfMonth(subMonths(now, 5)),
          end: endOfMonth(now),
        };
      case "year":
        return {
          start: startOfYear(now),
          end: endOfYear(now),
        };
      default:
        return null;
    }
  }, [searchParams, showDateFilter, dateFilterField]);

  // Optimize the filtered and sorted data calculation with useMemo
  const filteredAndSortedData = React.useMemo(() => {
    let result = [...initialData];

    // Filter out deleted items
    const deletedItemsArray = Array.from(deletedItems);
    result = result.filter(
      (item) => !deletedItemsArray.includes(getItemId(item))
    );

    // Apply date filter
    const dateRange = getDateRange();
    if (dateRange && dateFilterField) {
      result = result.filter((item) => {
        const itemDate = item[dateFilterField];
        if (!itemDate) return false;

        try {
          const date =
            itemDate instanceof Date
              ? itemDate
              : new Date(itemDate as string | number);
          if (isNaN(date.getTime())) return false;

          return isWithinInterval(date, {
            start: dateRange.start,
            end: dateRange.end,
          });
        } catch (error) {
          console.error("Error parsing date for filtering:", error);
          return false;
        }
      });
    }

    // Apply search filter
    if (searchTerm) {
      if (customFilter) {
        // Use custom filter when provided
        result = result.filter((item) => customFilter!(item, searchTerm));
      } else {
        // Use default title-based filter
        result = result.filter((item) =>
          getItemTitle(item).toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
    }

    // Always apply sorting
    result.sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      // Special handling for date fields
      if (
        sortField === "created" ||
        sortField === "updated" ||
        sortField === "timestamp"
      ) {
        try {
          const aDate = aValue
            ? (aValue instanceof Date
                ? aValue
                : new Date(aValue as string | number)
              ).getTime()
            : 0;
          const bDate = bValue
            ? (bValue instanceof Date
                ? bValue
                : new Date(bValue as string | number)
              ).getTime()
            : 0;

          // If either date is invalid, treat it as "unknown" and sort it last
          if (isNaN(aDate) && isNaN(bDate)) return 0;
          if (isNaN(aDate)) return 1;
          if (isNaN(bDate)) return -1;

          return sortOrder === "asc" ? aDate - bDate : bDate - aDate;
        } catch (error) {
          console.error("Error parsing date:", error);
          // If date parsing fails, treat as "unknown" and sort last
          if (!aValue && !bValue) return 0;
          if (!aValue) return 1;
          if (!bValue) return -1;
          return 0;
        }
      }

      // Handle undefined or null values
      if (!aValue && !bValue) return 0;
      if (!aValue) return 1;
      if (!bValue) return -1;

      // Safe comparison now that we've handled null/undefined
      const comparison =
        String(aValue) < String(bValue)
          ? -1
          : String(aValue) > String(bValue)
            ? 1
            : 0;
      return sortOrder === "asc" ? comparison : -comparison;
    });

    // Update total items count
    setTotalItems(result.length);
    return result;
  }, [
    initialData,
    searchTerm,
    sortField,
    sortOrder,
    getItemTitle,
    getItemId,
    deletedItems,
    customFilter,
    getDateRange,
    dateFilterField,
  ]);

  // Update the visibleData calculation to better handle the grouped items
  const visibleData = React.useMemo(() => {
    if (!groupingEnabled) {
      // No grouping, just use the standard filtered and sorted data
      return filteredAndSortedData.slice(0, visibleItems);
    }

    // When grouping is enabled, we need to keep the original sort order
    // but still divide into groups correctly
    return filteredAndSortedData.slice(0, visibleItems);
  }, [filteredAndSortedData, visibleItems, groupingEnabled]);

  // Handle initial load state
  React.useEffect(() => {
    if (isInitialLoad) {
      const timer = setTimeout(() => setIsInitialLoad(false), 50);
      return () => clearTimeout(timer);
    }
  }, [isInitialLoad]);

  // Add intersection observer for infinite scroll
  const observerTarget = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && visibleItems < totalItems) {
          setVisibleItems((prev) => Math.min(prev + 25, totalItems)); // Load 25 more items at a time
        }
      },
      { threshold: 0.5 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => observer.disconnect();
  }, [totalItems, visibleItems]);

  const handleAccordionChange = useCallback(
    (value: string[]) => {
      // Find newly added items (expanded items) and select them
      const newlyExpanded = value.filter(
        (id) => !expandedItems.includes(id) && id !== "new"
      );

      if (newlyExpanded.length > 0) {
        setSelectedItems((prev) => {
          // Create a Set to handle duplicates
          const selected = new Set([...prev]);
          // Add newly expanded items
          newlyExpanded.forEach((id) => selected.add(id));
          return Array.from(selected);
        });
      }

      // Update expanded items state
      setExpandedItems(value);
    },
    [expandedItems]
  );

  // Add a function to handle operation status updates
  const updateOperationStatus = useCallback(
    (
      id: string,
      type: OperationStatus["type"],
      status: OperationStatus["status"]
    ) => {
      setOperationStatus((prev) => ({
        ...prev,
        [id]: { type, status, timestamp: Date.now() },
      }));

      // Clear success status after 2 seconds
      if (status === "success") {
        setTimeout(() => {
          setOperationStatus((prev) => {
            const newStatus = { ...prev };
            delete newStatus[id];
            return newStatus;
          });
        }, 2000);
      }
    },
    []
  );

  const handleNew = useCallback(() => {
    if (!onCreate) return;
    setNewItem({} as T);
    setExpandedItems(["new"]);
    setSelectedItems([]);
  }, [onCreate]);

  const handleNewCancel = useCallback(() => {
    setNewItem(null);
    setExpandedItems([]);
  }, []);

  const handleCancel = useCallback((id: string) => {
    setExpandedItems((prev) => prev.filter((item) => item !== id));
  }, []);

  // Add a helper function to check if duplication is rate limited
  const isDuplicateRateLimited = useCallback(() => {
    const now = Date.now();
    return now - lastDuplicateTime < RATE_LIMIT_DELAY;
  }, [lastDuplicateTime]);

  const handleDuplicate = useCallback(
    async (item: T) => {
      if (isDuplicateRateLimited()) {
        toast({
          title: "Please wait",
          description: "You're duplicating items too quickly",
          variant: "default",
        } as ToastProps);
        return;
      }

      const itemId = getItemId(item);
      try {
        setLastDuplicateTime(Date.now());
        setDuplicatingItems((prev) => [...prev, itemId]);
        setExpandedItems([]);

        await onDuplicate?.(item);
        toast({
          title: "Success",
          description: "Item duplicated successfully",
          variant: "success",
        } as ToastProps);
      } catch (error) {
        console.error("Error duplicating item:", error);
        toast({
          title: "Error",
          description: "Failed to duplicate item",
          variant: "destructive",
        } as ToastProps);
      } finally {
        setDuplicatingItems((prev) => prev.filter((id) => id !== itemId));
      }
    },
    [onDuplicate, toast, isDuplicateRateLimited, getItemId]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      // If skipDeleteConfirm is true and showDeleteDialog is false, delete without confirmation
      if (skipDeleteConfirm && !showDeleteDialog) {
        try {
          // Remove from expanded items immediately
          setExpandedItems((prev) => prev.filter((itemId) => itemId !== id));

          // Close the delete alert and reset drawer states
          setDeleteAlertOpen(null);

          // Mark as pending
          updateOperationStatus(id, "delete", "pending");

          // Add to deleted items set
          setDeletedItems((prev) => new Set([...Array.from(prev), id]));

          await onDelete?.(id);

          // Remove from selected items
          setSelectedItems((prev) => prev.filter((itemId) => itemId !== id));

          updateOperationStatus(id, "delete", "success");
          toast({
            title: "Success",
            description: "Item deleted successfully",
            variant: "success",
          });
        } catch (error) {
          console.error("Error deleting item:", error);

          // Remove from deleted items on error
          setDeletedItems((prev) => {
            const newSet = new Set(Array.from(prev));
            newSet.delete(id);
            return newSet;
          });

          // Extract more meaningful error message if available
          let errorMessage = "Failed to delete item";
          if (error instanceof Error) {
            if (error.message.includes("associated orders")) {
              errorMessage =
                "This item has associated orders that must be addressed first";
            } else if (error.message.includes("foreign key constraint")) {
              errorMessage =
                "This item cannot be deleted because it is referenced by other records";
            } else if (error.message) {
              // Use the actual error message if available
              errorMessage = error.message;
            }
          }

          toast({
            title: "Unable to Delete",
            description: errorMessage,
            variant: "destructive",
          });
        }
        return;
      }

      // Original delete logic with confirmation
      try {
        // Remove from expanded items immediately
        setExpandedItems((prev) => prev.filter((itemId) => itemId !== id));

        // Close the delete alert and reset drawer states
        setDeleteAlertOpen(null);

        // Mark as pending
        updateOperationStatus(id, "delete", "pending");

        // Add to deleted items set
        setDeletedItems((prev) => new Set([...Array.from(prev), id]));

        await onDelete?.(id);

        // Remove from selected items
        setSelectedItems((prev) => prev.filter((itemId) => itemId !== id));

        updateOperationStatus(id, "delete", "success");
        toast({
          title: "Success",
          description: "Item deleted successfully",
          variant: "success",
        });
      } catch (error) {
        console.error("Error deleting item:", error);

        // Remove from deleted items on error
        setDeletedItems((prev) => {
          const newSet = new Set(Array.from(prev));
          newSet.delete(id);
          return newSet;
        });

        // Extract more meaningful error message if available
        let errorMessage = "Failed to delete item";
        if (error instanceof Error) {
          if (error.message.includes("associated orders")) {
            errorMessage =
              "This item has associated orders that must be addressed first";
          } else if (error.message.includes("foreign key constraint")) {
            errorMessage =
              "This item cannot be deleted because it is referenced by other records";
          } else if (error.message) {
            // Use the actual error message if available
            errorMessage = error.message;
          }
        }

        toast({
          title: "Unable to Delete",
          description: errorMessage,
          variant: "destructive",
        });
      }
    },
    [
      onDelete,
      toast,
      updateOperationStatus,
      showDeleteDialog,
      skipDeleteConfirm,
    ]
  );

  const handleBulkDelete = useCallback(async () => {
    try {
      // Add a single pending status for bulk delete
      const bulkId = "bulk-delete";
      updateOperationStatus(bulkId, "delete", "pending");

      // Add all selected items to deleted set
      setDeletedItems(
        (prev) => new Set([...Array.from(prev), ...selectedItems])
      );

      // Delete all items in a single operation
      await onDelete?.(selectedItems);

      // Show success status and notification
      updateOperationStatus(bulkId, "delete", "success");
      toast({
        title: "Success",
        description: `${selectedItems.length} items deleted successfully`,
        variant: "success",
      });

      // Clear selected items and close drawers
      setSelectedItems([]);
      setBulkDrawerOpen(false);
      setBulkDrawerHidden(false);
      setBulkDeleteAlertOpen(false);
    } catch (error) {
      console.error("Error deleting items:", error);
      // Remove selected items from deleted set on error
      setDeletedItems((prev) => {
        const newSet = new Set(Array.from(prev));
        selectedItems.forEach((id) => newSet.delete(id));
        return newSet;
      });

      // Extract more meaningful error message if available
      let errorMessage = "Failed to delete items";
      if (error instanceof Error) {
        if (error.message.includes("associated orders")) {
          errorMessage =
            "Some items have associated orders that must be addressed first";
        } else if (error.message.includes("foreign key constraint")) {
          errorMessage =
            "Some items cannot be deleted because they are referenced by other records";
        } else if (error.message) {
          // Use the actual error message if available
          errorMessage = error.message;
        }
      }

      toast({
        title: "Unable to Delete",
        description: errorMessage,
        variant: "destructive",
      });
    }
  }, [selectedItems, onDelete, toast, updateOperationStatus]);

  const toggleSelectAll = useCallback(() => {
    if (selectedItems.length === filteredAndSortedData.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredAndSortedData.map((item) => getItemId(item)));
    }
  }, [filteredAndSortedData, getItemId, selectedItems.length]);

  const toggleSelectItem = useCallback((id: string) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
    );
  }, []);

  // Add a helper function to check if an item is optimistic
  const isOptimisticItem = useCallback(
    (item: T) => {
      const id = getItemId(item);
      const status = operationStatus[id];
      // Consider an item optimistic only if it has a pending operation
      return status?.status === "pending";
    },
    [operationStatus, getItemId]
  );

  // Modify the render function to include status indicators
  const renderStatusIndicator = (id: string) => {
    const status = operationStatus[id];
    // Don't show status for duplicate operations
    if (!status || status.type === "duplicate") return null;

    return (
      <div className='ml-2 flex items-center'>
        {status.status === "pending" ? (
          <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
        ) : (
          <Check className='h-4 w-4 text-green-500' />
        )}
      </div>
    );
  };

  // Update the duplicate button rendering
  const renderDuplicateButton = (item: T) => {
    if (!hasCreatePermission || !onDuplicate) return null;

    const itemId = getItemId(item);
    const isLoading = duplicatingItems.includes(itemId);

    return (
      <Button
        variant='ghost'
        size='sm'
        onClick={(e) => {
          e.stopPropagation();
          handleDuplicate(item);
        }}
        disabled={isLoading || isOptimisticItem(item)}
      >
        {isLoading ? (
          <Loader2 className='h-4 w-4 animate-spin' />
        ) : (
          <Copy className='h-4 w-4' />
        )}
      </Button>
    );
  };

  const handleNewSubmit = async (formData: T): Promise<T> => {
    if (!onCreate) {
      throw new Error("Create operation not available");
    }
    try {
      const tempId = `temp-${Date.now()}`;
      updateOperationStatus(tempId, "create", "pending");
      const result = await onCreate(formData);
      updateOperationStatus(getItemId(result), "create", "success");
      setNewItem(null);
      setExpandedItems([]);
      toast({
        title: "Success",
        description: "Item created successfully",
        variant: "success",
      });
      return result;
    } catch (error) {
      console.error("Error creating item:", error);
      toast({
        title: "Error",
        description: "Failed to create item",
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleUpdate = async (id: string, data: T) => {
    try {
      updateOperationStatus(id, "update", "pending");
      const updatedItem = (await onUpdate?.(id, data)) as T;
      updateOperationStatus(id, "update", "success");
      // Close the accordion after successful update
      setExpandedItems((prev) => prev.filter((itemId) => itemId !== id));
      toast({
        title: "Success",
        description: "Item updated successfully",
        variant: "success",
      });
      return updatedItem;
    } catch (error) {
      console.error("Error updating item:", error);
      toast({
        title: "Error",
        description: "Failed to update item",
        variant: "destructive",
      });
      throw error;
    }
  };

  // Update the create button render logic
  const renderCreateButton = () => {
    if (!hasCreatePermission || !onCreate) return null;

    return (
      <Button
        onClick={handleNew}
        className='w-full md:w-auto'
        disabled={newItem !== null}
      >
        <Plus className='mr-2 h-4 w-4' /> New Item
      </Button>
    );
  };

  // Update the delete button rendering in desktop view
  const renderDesktopDeleteButton = (item: T, id: string) => {
    if (!hasDeletePermission) return null;

    if (skipDeleteConfirm && !showDeleteDialog) {
      return (
        <Button
          variant='ghost'
          size='sm'
          onClick={(e) => {
            e.stopPropagation();
            handleDelete(id);
          }}
          disabled={isOptimisticItem(item)}
          className='mr-4'
        >
          <Trash className='h-4 w-4 text-destructive' />
        </Button>
      );
    }

    return (
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            variant='ghost'
            size='sm'
            onClick={(e) => e.stopPropagation()}
            disabled={isOptimisticItem(item)}
            className='mr-4'
          >
            <Trash className='h-4 w-4 text-destructive' />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete this
              item.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className='flex items-center space-x-2 py-2'>
            <Checkbox
              id='dont-ask-again'
              checked={skipDeleteConfirm}
              onCheckedChange={(checked) => setSkipDeleteConfirm(!!checked)}
            />
            <label
              htmlFor='dont-ask-again'
              className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
            >
              Don&apos;t ask again until page reload
            </label>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (skipDeleteConfirm) {
                  setShowDeleteDialog(false);
                }
                handleDelete(id);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  };

  // Update the bulk delete button rendering
  const renderBulkDeleteButton = () => {
    if (!hasDeletePermission) return null;

    return (
      <AlertDialog
        open={bulkDeleteAlertOpen}
        onOpenChange={setBulkDeleteAlertOpen}
      >
        <AlertDialogTrigger asChild>
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              setBulkDeleteAlertOpen(true);
            }}
            className='text-destructive'
          >
            <Trash className='mr-2 h-4 w-4' />
            Delete Selected ({selectedItems.length})
          </DropdownMenuItem>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              selected items.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  };

  // Update the form rendering to respect permissions
  const renderForm = (item: T | null) => {
    if ((!hasCreatePermission || !onCreate) && item === null) return null;

    return (
      <FormComponent
        data={item}
        onSave={async (formData: T): Promise<T> => {
          try {
            if (item !== null) {
              if (!hasUpdatePermission) {
                throw new Error(
                  "You don't have permission to update this item"
                );
              }
              return handleUpdate(getItemId(item), formData);
            } else {
              if (!hasCreatePermission) {
                throw new Error("You don't have permission to create items");
              }
              return handleNewSubmit(formData);
            }
          } catch (error) {
            console.error("Error saving item:", error);
            toast({
              title: "Error",
              description:
                error instanceof Error ? error.message : "Failed to save item",
              variant: "destructive",
            });
            throw error;
          }
        }}
        onCancel={() =>
          item ? handleCancel(getItemId(item)) : handleNewCancel()
        }
        isNew={!item}
        canUpdate={hasUpdatePermission}
      />
    );
  };

  const handleExportCSV = useCallback(() => {
    const itemsToExport =
      selectedItems.length > 0
        ? filteredAndSortedData.filter((item) =>
            selectedItems.includes(getItemId(item))
          )
        : filteredAndSortedData;

    const now = new Date();
    const dateStr = now.toISOString().split("T")[0];
    const timeStr = now.toTimeString().split(" ")[0].replace(/:/g, "-");
    const fullFilename = `${exportFilename}_${dateStr}_${timeStr}`;

    exportToCSV(itemsToExport, fullFilename, exportableFields);

    // Close mobile drawer if open
    setBulkDrawerOpen(false);
  }, [
    selectedItems,
    filteredAndSortedData,
    getItemId,
    exportableFields,
    exportFilename,
  ]);

  // Export selected (or all) items to Cin7 CSV – only for product resource
  const handleExportCin7 = useCallback(() => {
    if (resource !== "products") return;

    const itemsToExport =
      selectedItems.length > 0
        ? filteredAndSortedData.filter((item) =>
            selectedItems.includes(getItemId(item))
          )
        : filteredAndSortedData;

    const now = new Date();
    const dateStr = now.toISOString().split("T")[0];
    const timeStr = now.toTimeString().split(" ")[0].replace(/:/g, "-");
    const filename = `Cin7Export_${dateStr}_${timeStr}`;

    // Type assertion is safe here because we've checked resource === "products"
    // and this function is only called from product-related components
    exportProductsToCin7(itemsToExport as unknown as Product[], filename);

    setBulkDrawerOpen(false);
  }, [resource, selectedItems, filteredAndSortedData, getItemId]);

  // Update the LoadingSkeleton to have the same exact structure as the loaded content
  const LoadingSkeleton = () => (
    <div>
      {Array.from({ length: 10 }).map((_, index) => (
        <div
          key={index}
          className='border-t md:border px-4 py-3 first:border-t'
          style={{
            position: "relative",
            zIndex: 1,
          }}
        >
          <div className='flex w-full items-center gap-4 min-h-[48px]'>
            {/* Checkbox skeleton - smaller size */}
            <div className='flex-shrink-0 w-4 h-4 rounded bg-muted animate-pulse' />

            {/* Content skeleton */}
            <div className='flex-1 min-w-0'>
              <div className='flex items-center gap-4'>
                {/* Avatar/Image skeleton */}
                <div className='h-8 w-8 rounded-sm bg-muted animate-pulse flex-shrink-0' />

                {/* Text content skeleton - Optimized for LCP */}
                <div className='flex-1 min-w-0 space-y-1'>
                  <div className='flex items-center gap-2'>
                    <div className='h-4 bg-muted rounded w-32 animate-pulse' />
                    <div className='h-5 w-16 bg-muted rounded-md animate-pulse hidden sm:block flex-shrink-0' />
                  </div>
                  <div className='h-3.5 bg-muted rounded w-24 animate-pulse' />
                </div>
              </div>
            </div>

            {/* Actions skeleton */}
            <div className='flex items-center gap-2 flex-shrink-0 min-w-[50px] justify-end'>
              <div className='h-8 w-8 rounded bg-muted animate-pulse' />
              <div className='h-8 w-8 rounded bg-muted animate-pulse mr-4' />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // Add a new function to render the mobile item actions drawer
  const renderMobileItemActionsDrawer = (item: T) => {
    const id = getItemId(item);
    return (
      <Drawer
        open={actionDrawerOpen === id}
        onOpenChange={(open) => {
          setActionDrawerOpen(open ? id : null);
          if (!open) {
            setActionDrawerHidden(false);
          }
        }}
      >
        <DrawerTrigger asChild>
          <Button
            variant='ghost'
            size='sm'
            onClick={(e) => {
              e.stopPropagation();
              setActionDrawerOpen(id);
            }}
          >
            <MoreVertical className='h-4 w-4' />
          </Button>
        </DrawerTrigger>
        <DrawerContent
          className={cn(
            "z-[101]",
            actionDrawerHidden && "translate-y-full transition-transform"
          )}
        >
          <div className='space-y-4 p-4'>
            <h3 className='font-semibold text-2xl text-center'>
              {getItemTitle(item)}
            </h3>
            <div className='space-y-2'>
              {hasCreatePermission && onDuplicate && (
                <Button
                  variant='outline'
                  className='w-full'
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDuplicate(item);
                    setActionDrawerOpen(null);
                  }}
                  disabled={
                    duplicatingItems.includes(getItemId(item)) ||
                    isOptimisticItem(item)
                  }
                >
                  {duplicatingItems.includes(getItemId(item)) ? (
                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  ) : (
                    <Copy className='mr-2 h-4 w-4' />
                  )}
                  Duplicate
                </Button>
              )}
              {hasDeletePermission && (
                <>
                  {skipDeleteConfirm && !showDeleteDialog ? (
                    <Button
                      variant='destructive'
                      className='w-full'
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDelete(id);
                        setActionDrawerOpen(null);
                      }}
                      disabled={isOptimisticItem(item)}
                    >
                      <Trash className='mr-2 h-4 w-4' />
                      Delete
                    </Button>
                  ) : (
                    <AlertDialog
                      open={deleteAlertOpen === id}
                      onOpenChange={(open) => {
                        setDeleteAlertOpen(open ? id : null);
                        setActionDrawerHidden(!open);
                      }}
                    >
                      <AlertDialogTrigger asChild>
                        <Button
                          variant='destructive'
                          className='w-full'
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setDeleteAlertOpen(id);
                            setActionDrawerHidden(true);
                          }}
                          disabled={isOptimisticItem(item)}
                        >
                          <Trash className='mr-2 h-4 w-4' />
                          Delete
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent
                        className={cn(actionDrawerHidden && "z-[102]")}
                      >
                        <AlertDialogHeader>
                          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This action cannot be undone. This will permanently
                            delete this item.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <div className='flex items-center space-x-2 py-2'>
                          <Checkbox
                            id='mobile-dont-ask-again'
                            checked={skipDeleteConfirm}
                            onCheckedChange={(checked) =>
                              setSkipDeleteConfirm(!!checked)
                            }
                          />
                          <label
                            htmlFor='mobile-dont-ask-again'
                            className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
                          >
                            Don&apos;t ask again until page reload
                          </label>
                        </div>
                        <AlertDialogFooter>
                          <AlertDialogCancel
                            onClick={() => setActionDrawerHidden(false)}
                          >
                            Cancel
                          </AlertDialogCancel>
                          <AlertDialogAction
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (skipDeleteConfirm) {
                                setShowDeleteDialog(false);
                              }
                              handleDelete(id);
                            }}
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </>
              )}
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    );
  };

  return (
    <div className='w-full space-y-1 md:space-y-2 pb-16'>
      {/* Date Filter */}
      {showDateFilter && (
        <div className='px-4 md:px-0 pb-2'>
          <DateFilter />
        </div>
      )}

      {/* Search and Filter Controls */}
      <SearchFilterControls
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        filterableFields={filterableFields}
        sortField={sortField}
        setSortField={(field) => setSortField(field as keyof T)}
        sortOrder={sortOrder}
        setSortOrder={(order) => setSortOrder(order)}
        groupingEnabled={groupingEnabled}
        setGroupingEnabled={(enabled) => setGroupingEnabled(enabled)}
        renderCreateButton={renderCreateButton}
        isMobile={isMobile}
      />

      {/* Items List */}
      {showCheckboxSelection && (
        <div className='flex items-center justify-between h-10 pl-4 mb-1'>
          <div className='flex items-center space-x-4'>
            <Checkbox
              checked={selectedItems.length === filteredAndSortedData.length}
              onCheckedChange={toggleSelectAll}
              id='select-all'
              name='select-all'
              className='w-4 h-4'
            />
            <span className='text-sm text-muted-foreground'>
              {selectedItems.length} selected
            </span>
          </div>
          {selectedItems.length > 0 && hasDeletePermission && (
            <>
              {isMobile ? (
                <>
                  <Button
                    variant='outline'
                    onClick={() => setBulkDrawerOpen(true)}
                  >
                    Bulk Actions
                  </Button>
                  {bulkDrawerOpen && (
                    <Drawer
                      open={bulkDrawerOpen}
                      onOpenChange={(open) => {
                        setBulkDrawerOpen(open);
                        if (!open) {
                          setBulkDrawerHidden(false);
                        }
                      }}
                    >
                      <DrawerContent
                        className={cn(
                          "z-[101]",
                          bulkDrawerHidden &&
                            "translate-y-full transition-transform"
                        )}
                      >
                        <div className='space-y-4 p-4'>
                          <h3 className='font-semibold text-2xl text-center'>
                            Bulk Actions
                          </h3>
                          <div className='space-y-2'>
                            <Button
                              variant='outline'
                              className='w-full'
                              onClick={handleExportCSV}
                            >
                              <FileDown className='mr-2 h-4 w-4' />
                              Export{" "}
                              {selectedItems.length > 0
                                ? `Selected (${selectedItems.length})`
                                : "All"}{" "}
                              to CSV
                            </Button>
                            {resource === "products" && (
                              <Button
                                variant='outline'
                                className='w-full'
                                onClick={handleExportCin7}
                              >
                                <FileDown className='mr-2 h-4 w-4' />
                                Export{" "}
                                {selectedItems.length > 0
                                  ? `Selected (${selectedItems.length})`
                                  : "All"}{" "}
                                to Cin7
                              </Button>
                            )}
                            {renderBulkDeleteButton()}
                          </div>
                        </div>
                      </DrawerContent>
                    </Drawer>
                  )}
                </>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant='outline'>Bulk Actions</Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align='end'>
                    <DropdownMenuItem onSelect={handleExportCSV}>
                      <FileDown className='mr-2 h-4 w-4' />
                      Export{" "}
                      {selectedItems.length > 0
                        ? `Selected (${selectedItems.length})`
                        : "All"}{" "}
                      to CSV
                    </DropdownMenuItem>
                    {resource === "products" && (
                      <DropdownMenuItem onSelect={handleExportCin7}>
                        <FileDown className='mr-2 h-4 w-4' />
                        Export{" "}
                        {selectedItems.length > 0
                          ? `Selected (${selectedItems.length})`
                          : "All"}{" "}
                        to Cin7
                      </DropdownMenuItem>
                    )}
                    {renderBulkDeleteButton()}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </>
          )}
        </div>
      )}

      {isInitialLoad ? (
        <LoadingSkeleton />
      ) : (
        <Accordion
          type='multiple'
          value={expandedItems}
          onValueChange={handleAccordionChange}
          className='space-y-0'
        >
          <div>
            {newItem && (
              <div key='new-item'>
                <AccordionItem
                  value='new'
                  className='border-t md:border px-4 first:border-t'
                >
                  <div className='py-4 min-h-[200px]'>{renderForm(null)}</div>
                </AccordionItem>
              </div>
            )}
            {visibleData.length === 0 ? (
              <div className='flex flex-col items-center justify-center py-8 text-center px-4'>
                <p className='text-muted-foreground mb-2'>No entries found</p>
                {searchTerm && (
                  <p className='text-sm text-muted-foreground'>
                    Try adjusting your search or filters
                  </p>
                )}
              </div>
            ) : (
              <>
                {visibleData.map((item, index) => {
                  const id = getItemId(item);
                  const isSelected = selectedItems.includes(id);
                  const imageUrl = getItemImage?.(item);

                  // Calculate if we need to show a group divider
                  const previousItem =
                    index > 0 ? visibleData[index - 1] : null;
                  const showGroupDivider =
                    groupingEnabled &&
                    hasGroupChanged(item, previousItem, sortField as string);
                  const groupInfo = getGroupKey(item, sortField as string);

                  return (
                    <React.Fragment key={id}>
                      {showGroupDivider && (
                        <GroupMarker label={groupInfo.display} />
                      )}
                      <div>
                        <AccordionItem
                          value={id}
                          className='border-t md:border px-4 first:border-t'
                        >
                          <AccordionTrigger className='hover:no-underline py-3'>
                            <div className='flex w-full items-center gap-4 min-h-[48px]'>
                              {showCheckboxSelection && (
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => toggleSelectItem(id)}
                                  onClick={(e) => e.stopPropagation()}
                                  className='flex-shrink-0 w-4 h-4'
                                  id={`select-item-${id}`}
                                  name={`select-item-${id}`}
                                />
                              )}
                              <div className='flex-1 min-w-0 gap-0 m-0'>
                                {renderItem ? (
                                  <div className='min-w-0'>
                                    {renderItem(item, isSelected, index)}
                                  </div>
                                ) : (
                                  <div className='flex items-center gap-4'>
                                    <Avatar className='h-8 w-8 flex-shrink-0'>
                                      <AvatarImage
                                        src={imageUrl}
                                        alt={getItemTitle(item)}
                                      />
                                      <AvatarFallback className='h-8 w-8'>
                                        {(getItemTitle(item) || "")
                                          .slice(0, 2)
                                          .toUpperCase()}
                                      </AvatarFallback>
                                    </Avatar>
                                    <span
                                      className='text-left truncate max-w-[calc(100%-4rem)]'
                                      style={{
                                        contentVisibility: "auto",
                                        containIntrinsicSize: "0 48px",
                                      }}
                                    >
                                      {getItemTitle(item)}
                                    </span>
                                  </div>
                                )}
                              </div>
                              <div className='flex items-center flex-shrink-0 min-w-[50px] justify-end'>
                                {renderStatusIndicator(id)}
                                {renderItemActions?.(item)}
                                {isMobile ? (
                                  renderMobileItemActionsDrawer(item)
                                ) : (
                                  <>
                                    {renderDuplicateButton(item)}
                                    {renderDesktopDeleteButton(item, id)}
                                  </>
                                )}
                              </div>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className='pt-4 min-h-[200px]'>
                              {renderForm(item)}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      </div>
                    </React.Fragment>
                  );
                })}
              </>
            )}
          </div>
        </Accordion>
      )}

      {/* Infinite scroll observer target */}
      {visibleItems < totalItems && (
        <div
          ref={observerTarget}
          className='h-10 flex items-center justify-center'
        >
          <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
        </div>
      )}
    </div>
  );
}

// Add display name for better debugging
DataViewer.displayName = "DataViewer";

// Memoize the entire component
export default React.memo(DataViewer) as unknown as typeof DataViewer;

/* 
Usage example with date filtering:

<DataViewer
  data={yourData}
  formComponent={YourFormComponent}
  getItemId={(item) => item.id}
  getItemTitle={(item) => item.title}
  filterableFields={['title', 'status', 'created']}
  showDateFilter={true}
  dateFilterField="created" // or "updated" or any date field
  onUpdate={handleUpdate}
  onCreate={handleCreate}
  onDelete={handleDelete}
  canCreate={true}
  canUpdate={true}
  canDelete={true}
/>

The date filter will automatically read URL parameters set by the DateFilter component:
- period: "all" | "today" | "yesterday" | "week" | "month" | "3months" | "quarter" | "6months" | "year" | "custom"
- startDate: ISO date string (used with custom period)
- endDate: ISO date string (used with custom period)
*/
